<template>
  <div
    class="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary to-purple-600 px-4"
  >
    <div
      class="max-w-md w-full bg-white dark:bg-slate-800 rounded-lg shadow-2xl p-8"
    >
      <!-- Loading State -->
      <div v-if="isVerifying" class="text-center">
        <div
          class="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"
        ></div>
        <h2 class="text-2xl font-bold text-gray-800 dark:text-white mb-2">
          Verifying Your Email...
        </h2>
        <p class="text-gray-600 dark:text-gray-300">Please wait a moment</p>
      </div>

      <!-- Success State -->
      <div v-else-if="verificationSuccess" class="text-center">
        <div class="mb-6">
          <div
            class="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center"
          >
            <svg
              class="w-10 h-10 text-green-500 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              ></path>
            </svg>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-gray-800 dark:text-white mb-2">
          Email Verified Successfully! ✅
        </h2>

        <p class="text-gray-600 dark:text-gray-300 mb-4">
          {{ verificationMessage }}
        </p>

        <div
          v-if="verificationData"
          class="bg-gray-50 dark:bg-slate-700 rounded-lg p-4 mb-6 text-left"
        >
          <p class="text-sm text-gray-600 dark:text-gray-300">
            <strong>Email:</strong> {{ verificationData.email }}
          </p>
          <p class="text-sm text-gray-600 dark:text-gray-300 mt-2">
            <strong>Type:</strong> {{ verificationData.entity_type }}
          </p>
        </div>

        <div class="space-y-3">
          <button
            @click="redirectToDashboard"
            class="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Go to Dashboard
          </button>
          <button
            @click="closeWindow"
            class="w-full bg-gray-200 hover:bg-gray-300 dark:bg-slate-600 dark:hover:bg-slate-500 text-gray-700 dark:text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Close This Window
          </button>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="verificationError" class="text-center">
        <div class="mb-6">
          <div
            class="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center"
          >
            <svg
              class="w-10 h-10 text-red-500 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </div>
        </div>

        <h2 class="text-2xl font-bold text-gray-800 dark:text-white mb-2">
          Verification Failed ❌
        </h2>

        <p class="text-gray-600 dark:text-gray-300 mb-6">
          {{ errorMessage }}
        </p>

        <div
          class="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-6"
        >
          <p class="text-sm text-yellow-800 dark:text-yellow-200">
            <strong>Common issues:</strong>
          </p>
          <ul
            class="text-sm text-yellow-700 dark:text-yellow-300 mt-2 space-y-1 text-left list-disc list-inside"
          >
            <li>The link may have expired (15 minutes)</li>
            <li>The link may have already been used</li>
            <li>The link may be invalid or corrupted</li>
          </ul>
        </div>

        <button
          @click="goToLogin"
          class="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-3 px-6 rounded-lg transition-colors"
        >
          Go to Login
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import http from "@/helper/http";

export default {
  name: "EmailVerificationView",
  setup() {
    const router = useRouter();
    const route = useRoute();

    const isVerifying = ref(true);
    const verificationSuccess = ref(false);
    const verificationError = ref(false);
    const verificationMessage = ref("");
    const errorMessage = ref("");
    const verificationData = ref(null);

    const verifyEmail = async (token) => {
      try {
        isVerifying.value = true;

        const response = await http.get(`/v1/email/verify/${token}`);

        verificationSuccess.value = true;
        verificationMessage.value =
          response.data.message || "Your email has been verified successfully!";
        verificationData.value = {
          email: response.data.email,
          entity_type: response.data.entity_type,
          entity_id: response.data.entity_id,
        };
      } catch (error) {
        verificationError.value = true;
        errorMessage.value =
          error.response?.data?.error?.message ||
          "Unable to verify your email. Please try again or use the verification code sent to your email.";
      } finally {
        isVerifying.value = false;
      }
    };

    const redirectToDashboard = () => {
      router.push({ name: "dashboard" });
    };

    const goToLogin = () => {
      // Redirect to root path (same as logout)
      window.location.replace("/");
    };

    const closeWindow = () => {
      window.close();
    };

    onMounted(() => {
      const token = route.query.token;

      if (!token) {
        verificationError.value = true;
        errorMessage.value = "No verification token found in the URL.";
        isVerifying.value = false;
        return;
      }

      verifyEmail(token);
    });

    return {
      isVerifying,
      verificationSuccess,
      verificationError,
      verificationMessage,
      errorMessage,
      verificationData,
      redirectToDashboard,
      goToLogin,
      closeWindow,
    };
  },
};
</script>

<style scoped>
/* Custom animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
